import { defineStore } from 'pinia';
import { ManageDirectDebitService as _manageDirectDebit } from '@/services/store/manage-direct-debit.service';
import { ref } from 'vue';
import {
  ApttusDirectDebitStatus,
  ApttusDirectDebitStatusResponse,
} from '@/interfaces/entities/apttus-direct-debit-status-response.interface';
import { useAccountStore } from '../account/account.state';
import { usePaymentStore } from '../payment/payment.state';
import {
  IApttusDirectDebitRequest,
  IAssetLineItemDirectDebitStatusesRequest,
  IAssetLineItemDirectDebitStatusRequest,
} from '@/interfaces/make-a-payment/make-a-payment.interface';
import { AccountServices as _account } from '@/services/store/account.services';
import { OwnedAssistanceProduct } from '@/interfaces';
import { FormEvents } from '@/enums/form-events.enum';
import { IAnalyticsForm, IFormAnalytics } from '@/interfaces/forms/analytics-interface';
import { PaymentMethod, PaymentTypes } from '@/enums/payment-types.enum';
import { useAnalyticsStore } from '../analytics/analytics.state';
import { BANK_ACCOUNT, CREDIT_CARD, CREDIT_OR_DEBIT_CARD, OFF, ON } from '@/constants/general-words.const';
import { UpdateType } from '@/enums/direct-debit.enum';

export const useManageDirectDebitStore = defineStore('ManageDirectDebit', () => {
  // ------------------------------------------------------------- //
  // Define State
  // ------------------------------------------------------------- //
  const accountStore = useAccountStore();
  const paymentStore = usePaymentStore();
  const analyticsStore = useAnalyticsStore();

  const directDebitStatus = ref<ApttusDirectDebitStatus>();
  const apttusDirectDebitStatusResponse = ref<ApttusDirectDebitStatusResponse>();
  const directDebitPaymentMethod = ref<PaymentMethod>();
  const directDebitPaymentMethodId = ref<string>();
  const apttusId = ref<string | null>(null);
  const rsaDirectDebitPageUrl = ref<string>();
  const assetLineItems = ref<IAssetLineItemDirectDebitStatusRequest[]>();
  const portfolioAssistanceProducts = ref<OwnedAssistanceProduct[]>();
  enum manageDirectDebitAnalyticsContants {
    PAY_BY_DIRECT_DEBIT = 'Pay by direct debit',
    DIRECT_DEBIT_SETUP = 'Direct debit set up',
    DIRECT_DEBIT_NOT_SETUP = 'Direct debit not set up',
    DIRECT_DEBIT_PAYMENT_UPDATED = 'Direct debit payment details updated',
    DIRECT_DEBIT_REMOVED = 'Direct debit removed',
    DIRECT_DEBIT_UPDATED = 'Direct debit updated',
  }

  async function setDirectDebitDetailsFromIdentity(): Promise<boolean> {
    try {
      const isAuthenticated = await accountStore.isAuthenticated;
      if (!isAuthenticated) return false;
      const identity = accountStore.identityDetails;
      if (identity == undefined) {
        return false;
      }

      if (identity.apttusId) {
        apttusId.value = identity.apttusId!;
        await accountStore.fetchIdentityDetails(true); // Force refresh before getting direct debit status
        const directDebitStatusResponse = await paymentStore.getApttusDirectDebitStatus(apttusId.value);
        apttusDirectDebitStatusResponse.value = directDebitStatusResponse;
        directDebitStatus.value = directDebitStatusResponse.status;
        if (directDebitStatusResponse.paymentMethodId) {
          directDebitPaymentMethodId.value = directDebitStatusResponse.paymentMethodId;
        }
        if (directDebitStatusResponse.paymentMethod) {
          directDebitPaymentMethod.value = directDebitStatusResponse.paymentMethod;
        }
      }
    } catch (ex) {
      console.warn('direct debit fetch failed', ex);
      return false;
    }

    return true;
  }

  function setAssetLineItemsToStore(items: IAssetLineItemDirectDebitStatusRequest[]): void {
    assetLineItems.value = items;
  }

  /**
   * Add Direct Debit Payment Method
   *
   * @param formData
   */
  const addDirectDebitPaymentMethod = async (paymentDetails: IApttusDirectDebitRequest) => {
    const response = await _manageDirectDebit.addDirectDebitToPrn(apttusId.value!, paymentDetails);
    return response;
  };

  /**
   * Update  Direct Debit Payment Method
   *
   * @param formData
   */
  const updateDirectDebitPaymentMethod = async (paymentDetails: IApttusDirectDebitRequest) => {
    const response = await _manageDirectDebit.updateDirectDebitPayment(
      apttusId.value!,
      directDebitPaymentMethodId.value!,
      paymentDetails
    );
    return response;
  };

  /**
   * Set Direct Debit Asset Line Item Statuses
   *
   * @param formData
   */
  const setDirectDebitAssetLineItemStatuses = async () => {
    const response = await _manageDirectDebit.setDirectDebitAssetLineItemStatuses(apttusId.value!, {
      items: assetLineItems.value,
    } as IAssetLineItemDirectDebitStatusesRequest);
    if (response.value) {
      setAssetLineItemsToStore(assetLineItems.value!);
    }
    return response;
  };

  const addDirectDebitAndModifySettings = async (paymentDetails: IApttusDirectDebitRequest) => {
    if (!paymentDetails) return;
    // Add Direct Debit settings
    await addDirectDebitPaymentMethod(paymentDetails);

    // Modify Asset line Items
    if (!assetLineItems.value || assetLineItems.value.length == 0) return;
    await setDirectDebitAssetLineItemStatuses();
  };

  /**
   * Set Direct Debit Asset Line Item Statuses
   *
   * @param formData
   */
  const deactivateDirectDebitPaymentMethod = async () => {
    const response = await _manageDirectDebit.deactivateDirectDebitPaymentMethod(
      apttusId.value!,
      directDebitPaymentMethodId.value!
    );
    return response;
  };

  /**
   * redirect to SS RSA DD
   *
   * @param formData
   */
  const redirectToRSADirectDebitPage = async (updateType?: UpdateType): Promise<boolean> => {
    const appendUpdatedQuery = (baseUrl: string): string => {
      const urlObj = new URL(baseUrl);
      if (updateType) urlObj.searchParams.set('updated', updateType);
      return urlObj.toString();
    };

    if (rsaDirectDebitPageUrl.value) {
      window.location.href = appendUpdatedQuery(rsaDirectDebitPageUrl.value);
      return true;
    }

    try {
      const url = await _manageDirectDebit.getSelfServiceRSAManagePaymentMethodPage();
      rsaDirectDebitPageUrl.value = url;
      window.location.href = appendUpdatedQuery(url);
      return true;
    } catch (error) {
      console.warn('Unable to retrieve self service roadside assistance Direct Debit URL: ', error);
      return false;
    }
  };

  /**
   * Get Portfolio assitance products from account
   *
   */
  const getPortfolioAssitanceProductsForDirectDebit = async (): Promise<OwnedAssistanceProduct[]> => {
    const products = await _account.getPortfolioAssitanceProductsForDirectDebit();
    if (!products) {
      return [];
    }
    portfolioAssistanceProducts.value = products;
    return products;
  };

  function pushAnalytics(options: {
    event: FormEvents;
    form?: IAnalyticsForm;
    vehicleDetail?: any;
    directDebitItemStatus?: boolean;
    appendStepName?: boolean;
    paymentDetailsUpdated?: boolean;
    field?: string;
  }) {
    const model: IFormAnalytics = {
      event: options.event,
      form: options.form || {},
    };

    if (options.event == FormEvents.START || options.event == FormEvents.COMPLETE) {
      if (options.paymentDetailsUpdated) {
        model.form['field'] = manageDirectDebitAnalyticsContants.DIRECT_DEBIT_PAYMENT_UPDATED;
      } else if (directDebitStatus.value == ApttusDirectDebitStatus.Current || options.event == FormEvents.COMPLETE) {
        model.form['field'] = manageDirectDebitAnalyticsContants.DIRECT_DEBIT_SETUP;
      } else {
        model.form['field'] = manageDirectDebitAnalyticsContants.DIRECT_DEBIT_NOT_SETUP;
      }
    }

    if (options.field) {
      model.form['field'] = options.field;
    }

    if (options.vehicleDetail) {
      model.navigation = {
        name: options.directDebitItemStatus
          ? `${manageDirectDebitAnalyticsContants.PAY_BY_DIRECT_DEBIT} - ${ON}`
          : `${manageDirectDebitAnalyticsContants.PAY_BY_DIRECT_DEBIT} - ${OFF}`,
      };
      model.form.detail = {
        makeName: options.vehicleDetail.make?.description ?? '',
        modelName: options.vehicleDetail.model?.description ?? '',
        vehicleYear: options.vehicleDetail.manufacturingYear ?? '',
      };
    }

    let appendStepName = '';

    if (directDebitPaymentMethod.value || paymentStore.selectedPaymentMethod) {
      if (
        directDebitPaymentMethod.value == PaymentMethod.CreditCard ||
        paymentStore.selectedPaymentMethod == PaymentTypes.CreditCard
      ) {
        appendStepName = CREDIT_OR_DEBIT_CARD;
        model.payment = {
          creditCardProvider: paymentStore.cardDetails?.cardType,
          paymentMethod: CREDIT_CARD,
        };
      } else if (
        directDebitPaymentMethod.value == PaymentMethod.BankAccount ||
        paymentStore.selectedPaymentMethod == PaymentTypes.BankAccount
      ) {
        appendStepName = BANK_ACCOUNT;
        model.payment = {
          paymentMethod: BANK_ACCOUNT,
        };
      }
    }

    analyticsStore.pushAnalytics(model, appendStepName || '');
  }

  return {
    apttusId,
    directDebitStatus,
    directDebitPaymentMethod,
    portfolioAssistanceProducts,
    apttusDirectDebitStatusResponse,
    getPortfolioAssitanceProductsForDirectDebit,
    setDirectDebitDetailsFromIdentity,
    setAssetLineItemsToStore,
    setDirectDebitAssetLineItemStatuses,
    addDirectDebitAndModifySettings,
    redirectToRSADirectDebitPage,
    deactivateDirectDebitPaymentMethod,
    updateDirectDebitPaymentMethod,
    pushAnalytics,
    manageDirectDebitAnalyticsContants,
  };
});
